import { useState, useEffect, useMemo, useCallback } from 'react';
import { FantasyDraftAssistant } from '../utils/draftAssistant';
import { usePlayers } from './usePlayers';
import { useProjections } from './useProjections';
import type { Player } from '../types/player';
import type { 
  LeagueSettings, 
  DraftState, 
  DraftRecommendation, 
  DraftStrategy,
  MockDraftResult
} from '../types/draft';

interface UseDraftAssistantOptions {
  userTeamId: number;
}

/**
 * Hook to provide draft assistant functionality with league settings customization
 */
export function useDraftAssistant(
  leagueSettings: LeagueSettings,
  options: UseDraftAssistantOptions
) {
  // Fetch player data
  const { players, loading: playersLoading, error: playersError } = usePlayers();
  const { projections, loading: projectionsLoading, error: projectionsError } = useProjections();
  
  // State
  const [draftState, setDraftState] = useState<DraftState>({
    currentPick: 1,
    userTeamId: options.userTeamId,
    draftedPlayers: [],
    availablePlayers: [],
    userRoster: [],
    nextUserPicks: [],
    round: 1,
    totalRounds: 15
  });
  
  const [recommendations, setRecommendations] = useState<DraftRecommendation[]>([]);
  const [draftStrategy, setDraftStrategy] = useState<DraftStrategy | null>(null);
  const [mockDraftResult, setMockDraftResult] = useState<MockDraftResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Prepare enhanced player data with projections
  const enhancedPlayers = useMemo((): Player[] => {
    if (playersLoading || projectionsLoading || !players || !projections) {
      return [];
    }
    
    console.log(`Preparing ${players.length} players with projections`);
    
    const enhanced = players.map(player => {
      const playerId = String(player.id);
      const projection = projections[playerId] || 0;
      return {
        ...player,
        projection,
        lastYearPoints: player.lastYearPoints || 0
      };
    });
    
    console.log(`Enhanced players ready: ${enhanced.length} total`);
    return enhanced;
  }, [players, projections, playersLoading, projectionsLoading]);

  // Initialize draft assistant when data is ready
  const draftAssistant = useMemo(() => {
    if (enhancedPlayers.length === 0) {
      console.log('No enhanced players available for draft assistant');
      return null;
    }
    
    try {
      console.log(`Creating draft assistant with ${enhancedPlayers.length} players`);
      const assistant = new FantasyDraftAssistant(leagueSettings, enhancedPlayers);
      console.log('Draft assistant created successfully');
      return assistant;
    } catch (err) {
      console.error('Error initializing draft assistant:', err);
      setError(`Failed to initialize draft assistant: ${err}`);
      return null;
    }
  }, [enhancedPlayers, leagueSettings]);
  
  // Initialize draft state when players are loaded
  useEffect(() => {
    if (enhancedPlayers.length > 0) {
      console.log('🔄 useDraftAssistant: Initializing draft state with enhanced players', 'userTeamId:', options.userTeamId);
      
      // Calculate next user picks in snake draft
      const calculateNextUserPicks = (currentPick: number, userPosition: number, teams: number) => {
        const picks = [];
        let round = Math.ceil(currentPick / teams);
        
        // Calculate next 3 user picks
        for (let i = 0; i < 3; i++) {
          let pick;
          if (round % 2 === 1) {
            // Odd round: pick = (round - 1) * teams + userPosition
            pick = (round - 1) * teams + userPosition;
          } else {
            // Even round: pick = round * teams - (userPosition - 1)
            pick = round * teams - (userPosition - 1);
          }
          
          if (pick >= currentPick) {
            picks.push(pick);
          }
          round++;
        }
        
        return picks;
      };

      const nextPicks = calculateNextUserPicks(1, options.userTeamId, leagueSettings.teams);

      setDraftState({
        currentPick: 1,
        userTeamId: options.userTeamId,
        draftedPlayers: [],
        availablePlayers: enhancedPlayers,
        userRoster: [],
        nextUserPicks: nextPicks,
        round: 1,
        totalRounds: 15
      });

      setError(null);
    }
  }, [enhancedPlayers, leagueSettings.teams, options.userTeamId]);

  // Get draft recommendations when assistant or state changes
  useEffect(() => {
    if (!draftAssistant || draftState.availablePlayers.length === 0) {
      console.log('No draft assistant or available players, clearing recommendations');
      setRecommendations([]);
      return;
    }

    try {
      console.log(`Getting recommendations for pick ${draftState.currentPick}`);
      const recs = draftAssistant.getDraftRecommendations(draftState, 8);
      console.log(`Got ${recs.length} recommendations`);
      setRecommendations(recs);
    } catch (err) {
      console.error('Error getting draft recommendations:', err);
      setError(`Failed to get recommendations: ${err}`);
      setRecommendations([]);
    }
  }, [draftAssistant, draftState]);

  // Get draft strategy
  useEffect(() => {
    if (!draftAssistant) {
      setDraftStrategy(null);
      return;
    }

    try {
      const strategy = draftAssistant.getDraftStrategy(options.userTeamId);
      setDraftStrategy(strategy);
    } catch (err) {
      console.error('Error getting draft strategy:', err);
      setDraftStrategy(null);
    }
  }, [draftAssistant, options.userTeamId]);

  // Make a pick
  const makePick = useCallback((playerId: string, teamId: number) => {
    const player = draftState.availablePlayers.find(p => String(p.id) === playerId);
    if (!player) {
      console.error(`Player ${playerId} not found in available players`);
      return;
    }

    console.log(`Making pick: ${player.name} for team ${teamId} at pick ${draftState.currentPick}`);

    setDraftState(prev => {
      const newAvailablePlayers = prev.availablePlayers.filter(p => String(p.id) !== playerId);
      const newDraftedPlayers = [...prev.draftedPlayers, { playerId, teamId, pick: prev.currentPick }];
      const newUserRoster = teamId === prev.userTeamId 
        ? [...prev.userRoster, player]
        : prev.userRoster;

      const nextPick = prev.currentPick + 1;
      const nextRound = Math.ceil(nextPick / leagueSettings.teams);

      // Recalculate next user picks
      const calculateNextUserPicks = (currentPick: number, userPosition: number, teams: number) => {
        const picks = [];
        let round = Math.ceil(currentPick / teams);
        
        // Calculate next 3 user picks
        for (let i = 0; i < 3; i++) {
          let pick;
          if (round % 2 === 1) {
            // Odd round: pick = (round - 1) * teams + userPosition
            pick = (round - 1) * teams + userPosition;
          } else {
            // Even round: pick = round * teams - (userPosition - 1)
            pick = round * teams - (userPosition - 1);
          }
          
          if (pick >= currentPick) {
            picks.push(pick);
          }
          round++;
        }
        
        return picks;
      };

      const nextUserPicks = calculateNextUserPicks(nextPick, prev.userTeamId, leagueSettings.teams);

      return {
        ...prev,
        currentPick: nextPick,
        round: nextRound,
        availablePlayers: newAvailablePlayers,
        draftedPlayers: newDraftedPlayers,
        userRoster: newUserRoster,
        nextUserPicks
      };
    });
  }, [draftState.availablePlayers, leagueSettings.teams]);

  // Reset draft
  const resetDraft = useCallback((userPosition?: number) => {
    if (enhancedPlayers.length === 0) return;

    const teamId = userPosition || options.userTeamId;
    console.log(`🔄 resetDraft called for team ${teamId}`);

    // Calculate next user picks
    const calculateNextUserPicks = (currentPick: number, userPosition: number, teams: number) => {
      const picks = [];
      let round = Math.ceil(currentPick / teams);
      
      // Calculate next 3 user picks
      for (let i = 0; i < 3; i++) {
        let pick;
        if (round % 2 === 1) {
          // Odd round: pick = (round - 1) * teams + userPosition
          pick = (round - 1) * teams + userPosition;
        } else {
          // Even round: pick = round * teams - (userPosition - 1)
          pick = round * teams - (userPosition - 1);
        }
        
        if (pick >= currentPick) {
          picks.push(pick);
        }
        round++;
      }
      
      return picks;
    };

    const nextPicks = calculateNextUserPicks(1, teamId, leagueSettings.teams);

    setDraftState({
      currentPick: 1,
      userTeamId: teamId,
      draftedPlayers: [],
      availablePlayers: enhancedPlayers,
      userRoster: [],
      nextUserPicks: nextPicks,
      round: 1,
      totalRounds: 15
    });

    setError(null);
  }, [enhancedPlayers, leagueSettings.teams, options.userTeamId]);

  // Run mock draft
  const runMockDraft = useCallback(() => {
    if (!draftAssistant) {
      setError('Draft assistant not initialized');
      return;
    }

    try {
      console.log('Running mock draft...');
      const result = draftAssistant.runMockDraft(options.userTeamId, leagueSettings.teams);
      setMockDraftResult(result);
      console.log('Mock draft completed:', result);
    } catch (err) {
      console.error('Error running mock draft:', err);
      setError(`Failed to run mock draft: ${err}`);
    }
  }, [draftAssistant, options.userTeamId, leagueSettings.teams]);

  // Update league settings
  const updateLeagueSettings = useCallback((newSettings: LeagueSettings) => {
    if (draftAssistant) {
      try {
        console.log('Updating league settings:', newSettings);
        draftAssistant.updateLeagueSettings(newSettings);
      } catch (err) {
        console.error('Error updating league settings:', err);
        setError(`Failed to update league settings: ${err}`);
      }
    }
  }, [draftAssistant]);

  const loading = playersLoading || projectionsLoading;
  const combinedError = error || playersError || projectionsError;

  // Set fallback error if no data after loading
  useEffect(() => {
    if (!loading && !combinedError && enhancedPlayers.length === 0) {
      setError('No draftable players available');
    }
  }, [loading, combinedError, enhancedPlayers.length]);

  return {
    recommendations,
    loading,
    error: combinedError,
    makePick,
    draftState,
    resetDraft,
    updateLeagueSettings,
    draftStrategy,
    mockDraftResult,
    draftAssistant
  };
} 